import React, { useRef } from 'react'
import { withStyles } from '@mui/styles'

import { useContainerSize } from '../../../core/utils/useContainerSize'
import { drawerStyles } from './drawer.styles'

/**
 * @typedef {object} DrawerProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {React.ReactNode} children - El contenido que se renderizará dentro del drawer cuando esté abierto.
 * @property {boolean} [float] - Si es `true`, el drawer flota sobre el contenido en lugar de desplazarlo. Utiliza dimensiones calculadas dinámicamente.
 * @property {boolean} open - Si es `true`, el drawer se muestra en su estado abierto (parcial o completo según `fullyOpened`).
 * @property {boolean} fullyOpened - Si es `true` y `open` también es `true`, el drawer se expande completamente. Si es `false`, se muestra parcialmente abierto.
 */

/**
 * Un componente de cajón lateral (drawer) con soporte para estados de apertura parcial y completa, y modo flotante.
 * 
 * Este drawer proporciona tres estados visuales:
 * - **Cerrado**: El drawer está oculto
 * - **Parcialmente abierto**: El drawer se muestra con un ancho reducido (cuando `open=true` y `fullyOpened=false`)
 * - **Completamente abierto**: El drawer se expande a su ancho completo (cuando `open=true` y `fullyOpened=true`)
 * 
 * El modo flotante (`float=true`) permite que el drawer se superponga al contenido principal en lugar de
 * desplazarlo, útil para interfaces donde el espacio es limitado. En este modo, el componente calcula
 * dinámicamente su altura usando el hook `useContainerSize`.
 * 
 * El contenido del drawer solo se renderiza cuando está abierto, optimizando el rendimiento.
 * 
 * @component
 * @example
 * const MyLayout = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const [isFullyOpen, setIsFullyOpen] = useState(false);
 * 
 *   return (
 *     <div>
 *       <button onClick={() => setIsOpen(!isOpen)}>Toggle Drawer</button>
 *       <button onClick={() => setIsFullyOpen(!isFullyOpen)}>Expand/Collapse</button>
 *       
 *       <Drawer open={isOpen} fullyOpened={isFullyOpen} float={true}>
 *         <nav>
 *           <ul>
 *             <li>Inicio</li>
 *             <li>Perfil</li>
 *             <li>Configuración</li>
 *           </ul>
 *         </nav>
 *       </Drawer>
 *     </div>
 *   );
 * };
 * 
 * @param {DrawerProps} props - Las props del componente Drawer.
 */
export const Drawer = withStyles(drawerStyles)(({
  classes,
  children,
  float,
  open,
  fullyOpened,
}) => {
  const boxRef = useRef()
  const containerSize = useContainerSize(boxRef)

  let classState = open ? (fullyOpened ? classes.fullyOpened : classes.opened) : classes.closed
  if (float) {
    classState += ' ' + classes.boxFloat
  }

  let headerContent = null
  return (
    <>
      <aside className={classes.box + ' ' + classState} ref={boxRef}>
        <header className={classes.header}>{headerContent}</header>
        {/*When floating, css cannot use parent's size so we get it from a hook and restyle inline*/}
        <main className={classes.main} style={float ? { height: containerSize.height } : null}>
          {open ? children : null}
        </main>
      </aside>
    </>
  )
})

export default Drawer
